/**
 * RS Customizer — Knotty Bracelet
 * Visual preview renderer for knotty bracelet customization.
 * Fetches metaobject data (backgrounds + letter images), injects a gallery
 * preview slide, and renders letter images as the user types.
 *
 * MTO input validation is handled by rs-product-mto-input.js. ATC gating is managed by rs-product-core.js.
 * This file handles ONLY the visual preview.
 */
(function () {
  'use strict';

  const METAOBJECT_TYPE = 'customizer_knotty_bracelet_set';
  const LIQUID_VIEW = 'metaobject-knotty-bracelet';

  const GRAPHQL_QUERY = `
    query getKnottyBraceletMetaobject($handle: MetaobjectHandleInput!) {
      metaobject(handle: $handle) {
        handle
        type
        fields {
          key
          value
          references(first: 250) {
            edges {
              node {
                ... on Metaobject {
                  fields {
                    key
                    value
                    reference {
                      ... on MediaImage {
                        image { url width height }
                      }
                    }
                    references(first: 250) {
                      edges {
                        node {
                          ... on Metaobject {
                            fields {
                              key
                              value
                              reference {
                                ... on MediaImage {
                                  image { url width height }
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `;

  let _injectedSlideId = null;
  let _metaobjectData = null;
  let _backgroundsData = null;
  let _currentVariantTitle = null;

  // ─── DOM helpers ───
  function _getMtoFields() {
    return document.querySelectorAll('[data-mto-field]');
  }

  function _getMtoText() {
    const fields = _getMtoFields();
    let text = '';
    fields.forEach(function (field) {
      text += field.value;
    });
    return text.trim().toUpperCase();
  }

  // ─── Data fetching ───
  async function _fetchBaseConfig() {
    const response = await fetch(window.RSProduct.config.productUrl + '?view=' + LIQUID_VIEW);
    return response.json();
  }

  async function _fetchAlphabets() {
    if (!window.RSGraphQL) return null;

    try {
      const data = await window.RSGraphQL.executeQuery(GRAPHQL_QUERY, {
        handle: { handle: window.RSProduct.config.productHandle, type: METAOBJECT_TYPE },
      });
      return _extractAlphabets(data);
    } catch (err) {
      console.error('[KnottyBracelet] GraphQL fetch failed:', err);
      return null;
    }
  }

  function _extractAlphabets(data) {
    if (!data || !data.metaobject) return {};

    const alphabets = {};
    const alphabetsField = data.metaobject.fields.find(function (f) {
      return f.key === 'alphabets';
    });

    if (!alphabetsField || !alphabetsField.references || !alphabetsField.references.edges) {
      return {};
    }

    alphabetsField.references.edges.forEach(function (edge) {
      const node = edge.node;
      const entry = {};

      node.fields.forEach(function (field) {
        switch (field.key) {
          case 'key':
            entry.key = field.value;
            break;
          case 'name':
            entry.name = field.value;
            break;
          case 'category':
            entry.category = field.value;
            break;
          case 'rotation':
            entry.rotation = parseInt(field.value, 10) || 0;
            break;
          case 'max_height':
            entry.max_height = parseInt(field.value, 10) || 50;
            break;
          case 'spacing_x':
            entry.spacing_x = parseInt(field.value, 10) || 1;
            break;
          case 'postion_x':
            entry.position_x = parseInt(field.value, 10) || 45;
            break;
          case 'position_y':
            entry.position_y = parseInt(field.value, 10) || 50;
            break;
          case 'letters':
            entry.letters = {};
            if (field.references && field.references.edges) {
              field.references.edges.forEach(function (letterEdge) {
                const letterNode = letterEdge.node;
                let letterKey = null;
                let letterUrl = null;
                let w = 100;
                let h = 100;

                letterNode.fields.forEach(function (lf) {
                  if (lf.key === 'key') letterKey = lf.value;
                  if (lf.key === 'image' && lf.reference && lf.reference.image) {
                    letterUrl = lf.reference.image.url;
                    w = lf.reference.image.width || 100;
                    h = lf.reference.image.height || 100;
                  }
                });

                if (letterKey && letterUrl) {
                  entry.letters[letterKey] = { url: letterUrl, width: w, height: h };
                }
              });
            }
            break;
        }
      });

      if (entry.key && entry.category) {
        if (!alphabets[entry.key]) alphabets[entry.key] = {};
        alphabets[entry.key][entry.category] = {
          name: entry.name,
          key: entry.key,
          rotation: entry.rotation,
          max_height: entry.max_height,
          position_x: entry.position_x,
          position_y: entry.position_y,
          spacing_x: entry.spacing_x,
          letters: entry.letters || {},
        };
      }
    });

    return alphabets;
  }

  // ─── Background management ───
  function _getBackgroundForVariant(variantTitle) {
    if (!_backgroundsData || !variantTitle) return null;

    const key = variantTitle.toLowerCase();
    let match = null;

    Object.entries(_backgroundsData).forEach(function (pair) {
      if (pair[0].toLowerCase() === key || key.includes(pair[0].toLowerCase())) {
        match = pair[1];
      }
    });

    return match;
  }

  // ─── Letter rendering ───
  function _getAlphabetData() {
    if (!_metaobjectData || !_metaobjectData.alphabets || !_currentVariantTitle) return null;

    const variantAlphabet = _metaobjectData.alphabets[_currentVariantTitle];
    if (!variantAlphabet) return null;

    if (variantAlphabet.single_letter) return { type: 'single', data: variantAlphabet.single_letter };
    if (variantAlphabet.single_digit) return { type: 'single', data: variantAlphabet.single_digit };
    if (variantAlphabet.double_letter) return { type: 'double', data: variantAlphabet.double_letter };
    if (variantAlphabet.double_digit) return { type: 'double', data: variantAlphabet.double_digit };

    const firstKey = Object.keys(variantAlphabet)[0];
    if (firstKey) return { type: 'single', data: variantAlphabet[firstKey] };

    return null;
  }

  function _renderLetters() {
    const text = _getMtoText();
    const container = _getLetterContainer();
    if (!container) return;

    container.innerHTML = '';

    if (!text) return;

    const alphabet = _getAlphabetData();
    if (!alphabet || !alphabet.data || !alphabet.data.letters) return;

    if (alphabet.type === 'double' && text.length === 2) {
      _renderDoubleDigit(container, text, alphabet.data);
    } else {
      _renderSingleCharacters(container, text, alphabet.data);
    }
  }

  function _renderSingleCharacters(container, text, categoryData) {
    for (let i = 0; i < text.length; i++) {
      const letter = text[i];
      const letterData = categoryData.letters[letter];
      if (!letterData) continue;

      const imageUrl = typeof letterData === 'string' ? letterData : letterData.url;
      const xPosition = (categoryData.position_x || 50) + (i * 5);
      const yPosition = categoryData.position_y || 50;
      const rotation = categoryData.rotation || 0;
      const maxHeight = categoryData.max_height || 50;

      _createLetterImg(container, {
        src: imageUrl,
        alt: 'Letter ' + letter,
        left: xPosition,
        top: yPosition,
        rotation: rotation,
        height: maxHeight,
      });
    }
  }

  function _renderDoubleDigit(container, text, categoryData) {
    const d1 = text[0];
    const d2 = text[1];
    const d1Data = categoryData.letters[d1];
    const d2Data = categoryData.letters[d2];
    if (!d1Data || !d2Data) return;

    const containerRect = container.getBoundingClientRect();
    const heightPercent = categoryData.max_height || 75;
    const actualHeight = (heightPercent / 100) * containerRect.height;

    const d1Width = _calcDigitWidth(d1Data, actualHeight);
    const d2Width = _calcDigitWidth(d2Data, actualHeight);
    const spacing = categoryData.spacing_x || 1;
    const totalWidth = d1Width + d2Width + spacing;

    const centerX = ((categoryData.position_x || 50) / 100) * containerRect.width;
    const startX = centerX - (totalWidth / 2);

    const d1X = ((startX + d1Width / 2) / containerRect.width) * 100;
    const d2X = ((startX + d1Width + spacing + d2Width / 2) / containerRect.width) * 100;

    const baseRotation = categoryData.rotation || 0;

    _createLetterImg(container, {
      src: typeof d1Data === 'string' ? d1Data : d1Data.url,
      alt: 'Digit ' + d1,
      left: d1X,
      top: categoryData.position_y || 50,
      rotation: baseRotation,
      height: heightPercent,
    });

    _createLetterImg(container, {
      src: typeof d2Data === 'string' ? d2Data : d2Data.url,
      alt: 'Digit ' + d2,
      left: d2X,
      top: categoryData.position_y || 50,
      rotation: -baseRotation,
      height: heightPercent,
    });
  }

  function _calcDigitWidth(digitData, heightPx) {
    const w = typeof digitData === 'object' ? digitData.width : 100;
    const h = typeof digitData === 'object' ? digitData.height : 100;
    return heightPx * (w / h);
  }

  function _createLetterImg(container, opts) {
    const img = document.createElement('img');
    img.src = opts.src;
    img.alt = opts.alt;
    img.className = 'rs-knotty__letter';
    img.style.left = opts.left + '%';
    img.style.top = opts.top + '%';
    img.style.transform = 'translate(-50%,-50%) rotate(' + opts.rotation + 'deg)';
    img.style.height = opts.height + '%';
    container.appendChild(img);
  }

  // ─── Gallery slide letter container ───
  function _getLetterContainer() {
    if (!_injectedSlideId) return null;

    const slide = document.querySelector('[data-rs-injected="' + _injectedSlideId + '"]');
    if (!slide) return null;

    const customizerView = slide.querySelector('.customizer-view');
    if (!customizerView) return null;

    let grid = customizerView.querySelector('.rs-knotty__letters');
    if (!grid) {
      grid = document.createElement('div');
      grid.className = 'rs-knotty__letters';
      customizerView.appendChild(grid);
    }

    return grid;
  }

  // ─── Event handlers ───
  function _handleInput(event) {
    if (!event.target.matches('[data-mto-field]')) return;
    _renderLetters();
  }

  function _handleInputFocus(event) {
    if (!event.target.matches('[data-mto-field]')) return;
    if (_injectedSlideId) {
      window.RSProduct.gallery.navigate(_injectedSlideId);
    }
  }

  function _handleVariantChange(event) {
    if (!event.data || event.data.sectionId !== window.RSProduct.sectionId) return;

    const variant = event.data.variant;
    if (!variant || !variant.title) return;

    _currentVariantTitle = variant.title;

    // Gallery was replaced by rs-product-core's variant handler — re-inject the slide
    if (_injectedSlideId) {
      window.RSProduct.gallery.remove(_injectedSlideId);
      _injectedSlideId = null;
    }

    const backgroundUrl = _getBackgroundForVariant(_currentVariantTitle);
    if (backgroundUrl) {
      _injectedSlideId = window.RSProduct.gallery.inject(backgroundUrl, 1);
    }

    // If user already has text entered, re-render letters and navigate to preview
    const existingText = _getMtoText();
    if (existingText) {
      _renderLetters();
      if (_injectedSlideId) {
        window.RSProduct.gallery.navigate(_injectedSlideId);
      }
    }
  }

  // ─── Initialization ───
  async function _init() {
    if (!window.RSProduct.config.productHandle) return;

    let baseConfig, alphabets;
    try {
      [baseConfig, alphabets] = await Promise.all([_fetchBaseConfig(), _fetchAlphabets()]);
    } catch (err) {
      console.error('[KnottyBracelet] Data fetch failed:', err);
      return;
    }

    _backgroundsData = baseConfig.backgrounds || {};
    _metaobjectData = { ...baseConfig, alphabets: alphabets || {} };

    // Determine initial variant title from config
    const initialVariantId = String(window.RSProduct.config.initialVariantId || '');
    const variantNames = baseConfig.variant_names || {};
    const fullName = variantNames[initialVariantId] || '';
    const parts = fullName.split(' - ');
    _currentVariantTitle = parts.length > 1 ? parts[parts.length - 1] : fullName;

    // Inject preview slide at index 1 (2nd gallery position)
    const backgroundUrl = _getBackgroundForVariant(_currentVariantTitle);
    if (backgroundUrl) {
      _injectedSlideId = window.RSProduct.gallery.inject(backgroundUrl, 1);
    }

    // Listen for MTO input events
    document.addEventListener('input', _handleInput);
    document.addEventListener('focusin', _handleInputFocus);

    // Subscribe to variant changes
    if (window.subscribe && window.theme && window.theme.PUB_SUB_EVENTS) {
      window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, _handleVariantChange);
    }

    // Render letters if MTO field already has a value
    const existingText = _getMtoText();
    if (existingText) {
      _renderLetters();
    }
  }

  document.addEventListener('DOMContentLoaded', function () {
    if (!window.RSProduct) return;
    _init();
  });
})();
